import React, { ReactNode, useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import correcoesService, {
  CorrecaoDetailsResponse,
  CorrecaoDetailsSlotResponse,
} from "../../../api/correcoes";
import { RelatorioNotasDaTurmaNotaResponse } from "../../../api/relatorios";
import { errorToString } from "../../../helpers/funcoes";
import "./ModalCorrecoes.css";

export function ModalCorrecoes(props: {
  nota?: RelatorioNotasDaTurmaNotaResponse;
  onClose: () => void;
  onError: (error: string) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [correcoes, setCorrecoes] = useState<CorrecaoDetailsResponse[]>([]);
  const [activeTabId, setActiveTabId] = useState("");
  const [activeSlot, setActiveSlot] = useState<CorrecaoDetailsSlotResponse>();

  const ids = props.nota?.correcoes.map((x) => x.id) ?? [];

  useEffect(() => {
    setLoading(true);
    Promise.all(ids.map((i) => correcoesService.getDetails(i)))
      .then((res) => {
        res.sort((a, b) => a.pageIndex - b.pageIndex);
        if (res.length > 0) {
          setActiveTabId(res[0].id);
        }
        setCorrecoes(res);
      })
      .catch((err) => props.onError(errorToString(err)))
      .finally(() => setLoading(false));
    return () => {
      setCorrecoes([]);
      setActiveSlot(undefined);
    };
  }, [ids.join(",")]);

  const handleTabClick = (tabId: string) => {
    if (activeTabId != tabId) {
      setActiveTabId(tabId);
      setActiveSlot(undefined);
    }
  };

  const handleSlotClick = (slot: CorrecaoDetailsSlotResponse) => {
    setActiveSlot(slot == activeSlot ? undefined : slot);
  };

  let content: ReactNode;

  if (loading) {
    content = <Spinner />;
  } else if (correcoes.length == 0) {
    content = <>Nenhuma correção encontrada</>;
  } else {
    content = (
      <TabContent activeTab={activeTabId}>
        {correcoes.map((c) => (
          <TabPane key={c.id} tabId={c.id}>
            <CorrecaoPage
              correcao={c}
              activeSlot={activeSlot}
              onSlotClick={(s) => handleSlotClick(s)}
            />
          </TabPane>
        ))}
      </TabContent>
    );
  }

  return (
    <Modal
      isOpen={loading || correcoes.length > 0}
      toggle={props.onClose}
      scrollable
      fullscreen
    >
      <ModalHeader tag="div" toggle={props.onClose}>
        {correcoes.length > 0 && (
          <Nav pills>
            {correcoes.map((c) => (
              <NavItem key={c.id}>
                <NavLink
                  href="#"
                  className={c.id == activeTabId ? "active" : ""}
                  onClick={() => handleTabClick(c.id)}
                >
                  Página {c.pageIndex + 1}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        )}
      </ModalHeader>
      <ModalBody className="text-center">{content}</ModalBody>
      {activeSlot && (
        <ModalFooter>
          <div style={{ flexGrow: 1 }}>
            Gabarito: <span className="fw-bold">{activeSlot.gabarito}</span> |
            Leitura:{" "}
            <span className="fw-bold">{activeSlot.leitura.join(" ou ")}</span>
          </div>
        </ModalFooter>
      )}
    </Modal>
  );
}

function CorrecaoPage(props: {
  correcao: CorrecaoDetailsResponse;
  activeSlot?: CorrecaoDetailsSlotResponse;
  onSlotClick: (slot: CorrecaoDetailsSlotResponse) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [imageSize, setImageSize] = useState({ w: 0, h: 0 });
  const [scaleFactor, setScaleFactor] = useState(1);

  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setLoading(true);
    setImageSize({ w: 0, h: 0 });
  }, [props.correcao.id]);

  useEffect(() => {
    const w = imageRef.current?.width;
    setScaleFactor(w && imageSize.w > 0 ? w / imageSize.w : 1);
    return () => setScaleFactor(1);
  });

  const handleOnload = (img: HTMLImageElement) => {
    if (loading) {
      setLoading(false);
      setImageSize({ w: img.naturalWidth, h: img.naturalHeight });
    }
  };

  const scale = (value: number) => {
    return Math.round(value * scaleFactor);
  };

  return (
    <div className="p-2 d-flex justify-content-center">
      {loading && <Spinner />}
      <div
        style={{
          maxWidth: `min(${imageSize.w}px, 100%)`,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <img
          style={{
            width: "100%",
          }}
          ref={imageRef}
          src={props.correcao.imagemUri}
          onLoad={(e) => handleOnload(e.currentTarget)}
        />
        {props.correcao.slots.map((s, i) => (
          <span
            key={i}
            className={`modalCorrecoesSlot ${
              props.activeSlot === s ? "modalCorrecoesSlotActive" : ""
            }`}
            onClick={() => props.onSlotClick(s)}
            style={{
              left: `${scale(s.coordenadas[0])}px`,
              top: `${scale(s.coordenadas[1])}px`,
              width: `${scale(s.coordenadas[2] - s.coordenadas[0])}px`,
              height: `${scale(s.coordenadas[3] - s.coordenadas[1])}px`,
              borderColor: s.leitura.every((x) => x == "")
                ? "orange"
                : compararRespostas(s.gabarito, s.leitura)
                ? "green"
                : "red",
            }}
          />
        ))}
      </div>
    </div>
  );
}

function compararRespostas(gabarito: string, leitura: string[]): boolean {
  gabarito = trimZero(gabarito);
  for (const l of leitura) {
    if (l != "" && trimZero(l) == gabarito) return true;
  }
  return false;
}

function trimZero(value: string): string {
  if (value.includes(",")) {
    while (value.endsWith("0")) {
      value = value.substring(0, value.length - 1);
    }
    if (value.endsWith(",")) {
      return value.substring(0, value.length - 1);
    }
  }
  return value;
}
